import { ChevronLeft, ChevronRight, ExternalLink } from "lucide-react";
import {
  PaymentOrderItem,
  AppointmentPaymentItem,
  ExpenseItem,
  BankDepositItem,
  PaginationInfo,
} from "../../types/transactionHistory.types";

type TableDataItem = PaymentOrderItem | AppointmentPaymentItem | ExpenseItem | BankDepositItem;

interface TransactionTableProps {
  type: string;
  data: TableDataItem[];
  pagination: PaginationInfo | null;
  loading: boolean;
  currentPage: number;
  onPageChange: (page: number) => void;
  onRowClick?: (item: TableDataItem) => void;
}

function formatDate(dateStr: string | null | undefined): string {
  if (!dateStr) return "—";
  const d = new Date(dateStr);
  return d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
}

function formatCurrency(amount: number | undefined): string {
  if (amount === undefined || amount === null) return "—";
  const prefix = amount < 0 ? "-₹" : "₹";
  return `${prefix}${Math.abs(amount).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;
}

function getStatusBadge(status?: string): { text: string; className: string } {
  const s = status?.toLowerCase() || "";
  if (s.includes("completed") || s.includes("delivered")) {
    return { text: status || "Completed", className: "bg-emerald-50 text-emerald-700" };
  }
  if (s.includes("return")) {
    return { text: status || "Returned", className: "bg-red-50 text-red-700" };
  }
  if (s.includes("booked") || s.includes("consultation")) {
    return { text: status || "Booked", className: "bg-blue-50 text-blue-700" };
  }
  if (s.includes("generated") || s.includes("collected")) {
    return { text: status || "Processing", className: "bg-purple-50 text-purple-700" };
  }
  return { text: status || "—", className: "bg-gray-50 text-gray-700" };
}

function getPaymentBadge(method?: string): string {
  const m = method?.toLowerCase() || "";
  if (m === "cash") return "text-green-700";
  if (m === "online" || m === "upi") return "text-blue-700";
  if (m === "split") return "text-orange-700";
  return "text-gray-700";
}

function getOrderTypeLabel(type?: string): string {
  if (!type) return "Order";
  const labels: Record<string, string> = {
    "online-order": "Online",
    "offline-order": "Offline",
    "labtest-order": "Lab Test",
    "offline-labtest": "Offline Lab",
    "online-return": "Return",
    "offline-return": "Offline Return",
  };
  return labels[type] || type;
}

// ─── Render columns by type ─────────────────────────────────

function renderExpenseRow(item: any, onRowClick?: (item: TableDataItem) => void) {
  const id = item?.id || Math.random();
  const expenseType = item?.expenseType || item?.expense_type || "Other";
  const date = item?.expenseDate || item?.expense_date || item?.created_at || item?.createdAt || null;
  const paymentMethod = item?.paymentMethod || item?.payment_method || "—";
  const description = item?.description || "—";
  const amount = Number(item?.amount || 0);

  return (
    <tr
      key={id}
      onClick={() => onRowClick?.(item)}
      className="border-b border-gray-50 hover:bg-gray-50/50 transition-colors cursor-pointer"
    >
      <td className="py-3 px-4 text-xs text-gray-600">{formatDate(date)}</td>
      <td className="py-3 px-4 text-xs text-gray-500 font-mono">TXN-{id}</td>
      <td className="py-3 px-4">
        <span className="inline-block px-2 py-0.5 text-[10px] font-medium rounded-full bg-orange-50 text-orange-700">
          {expenseType}
        </span>
      </td>
      <td className="py-3 px-4 text-xs text-gray-500 max-w-[200px] truncate">{description}</td>
      <td className="py-3 px-4 text-xs">
        <span className={getPaymentBadge(paymentMethod)}>· {paymentMethod}</span>
      </td>
      <td className="py-3 px-4 text-xs font-semibold text-red-500 text-right">-{formatCurrency(amount)}</td>
    </tr>
  );
}

function renderOrderRow(item: any, onRowClick?: (item: TableDataItem) => void) {
  const orderType = item?.orderType || item?.order_type || item?.type || "";
  const status = item?.status || "";
  const badge = getStatusBadge(status);
  const isReturn = (orderType || "").toLowerCase().includes("return") || status.toLowerCase().includes("return");
  const orderNumber = item?.orderNumber || item?.order_number || item?.invoice_number || (item?.orderId ? `ORD-${item.orderId}` : "—");
  const date = item?.date || item?.order_date || item?.created_at || item?.createdAt || null;
  const patientName = item?.patientName || item?.patient_name || item?.patientPhone || item?.patient_phone || "—";
  const doctorName = item?.doctorName || item?.doctor_name || null;
  const paymentMethod = item?.paymentMethod || item?.payment_method || item?.payment_type || "—";
  const amount = Number(item?.amount !== undefined ? item.amount : (item?.totalAmount || item?.total_amount || 0));

  return (
    <tr
      key={`${orderType}-${item?.orderId || item?.id || orderNumber || Math.random()}`}
      onClick={() => onRowClick?.(item)}
      className="border-b border-gray-50 hover:bg-gray-50/50 transition-colors cursor-pointer group"
    >
      <td className="py-3 px-4 text-xs text-gray-600">{formatDate(date)}</td>
      <td className="py-3 px-4 text-xs text-gray-500 font-mono">{orderNumber}</td>
      <td className="py-3 px-4">
        <span className={`inline-block px-2 py-0.5 text-[10px] font-medium rounded-full ${badge.className}`}>
          {getOrderTypeLabel(orderType)}
        </span>
      </td>
      <td className="py-3 px-4 text-xs text-gray-600">
        {patientName}
        {doctorName && <span className="text-gray-400 ml-1">· {doctorName}</span>}
      </td>
      <td className="py-3 px-4 text-xs">
        <span className={getPaymentBadge(paymentMethod)}>· {paymentMethod}</span>
      </td>
      <td className="py-3 px-4 text-xs font-semibold text-right flex items-center justify-end gap-1">
        <span className={isReturn ? "text-red-500" : "text-emerald-600"}>
          {isReturn ? "-" : "+"}{formatCurrency(amount)}
        </span>
        <ExternalLink className="w-3 h-3 text-gray-300 opacity-0 group-hover:opacity-100 transition-opacity" />
      </td>
    </tr>
  );
}

function renderAppointmentRow(item: any, onRowClick?: (item: TableDataItem) => void) {
  const status = item?.status || "";
  const badge = getStatusBadge(status);
  const onlineAmount = Number(item?.onlineAmount || item?.online_amount || 0);
  const cashAmount = Number(item?.cashAmount || item?.cash_amount || 0);
  const amount = Number(item?.amount || (onlineAmount + cashAmount) || 0);
  const mode = onlineAmount > 0 && cashAmount > 0 ? "Split" : onlineAmount > 0 ? "Online" : "Cash";
  const date = item?.appointmentDate || item?.appointment_date || item?.paymentDate || item?.payment_date || null;
  const apptId = item?.appointmentId || item?.appointment_id || item?.id || "—";
  const startTime = item?.startTime || item?.start_time || null;
  const endTime = item?.endTime || item?.end_time || null;

  return (
    <tr
      key={apptId}
      onClick={() => onRowClick?.(item)}
      className="border-b border-gray-50 hover:bg-gray-50/50 transition-colors cursor-pointer group"
    >
      <td className="py-3 px-4 text-xs text-gray-600">{formatDate(date)}</td>
      <td className="py-3 px-4 text-xs text-gray-500 font-mono">{typeof apptId === "string" ? apptId.slice(0, 12) : apptId}...</td>
      <td className="py-3 px-4">
        <span className={`inline-block px-2 py-0.5 text-[10px] font-medium rounded-full ${badge.className}`}>
          {status || "Completed"}
        </span>
      </td>
      <td className="py-3 px-4 text-xs text-gray-600">
        {startTime && endTime ? `${startTime} - ${endTime}` : "—"}
      </td>
      <td className="py-3 px-4 text-xs">
        <span className={getPaymentBadge(mode)}>· {mode}</span>
      </td>
      <td className="py-3 px-4 text-xs font-semibold text-emerald-600 text-right flex items-center justify-end gap-1">
        +{formatCurrency(amount)}
        <ExternalLink className="w-3 h-3 text-gray-300 opacity-0 group-hover:opacity-100 transition-opacity" />
      </td>
    </tr>
  );
}

function renderDepositRow(item: any, onRowClick?: (item: TableDataItem) => void) {
  const id = item?.id || Math.random();
  const date = item?.depositDate || item?.deposit_date || item?.created_at || item?.createdAt || null;
  const refNo = item?.referenceNo || item?.reference_no || `DEP-${id}`;
  const description = item?.description || "—";
  const amount = Number(item?.amount || 0);

  return (
    <tr
      key={id}
      onClick={() => onRowClick?.(item)}
      className="border-b border-gray-50 hover:bg-gray-50/50 transition-colors cursor-pointer"
    >
      <td className="py-3 px-4 text-xs text-gray-600">{formatDate(date)}</td>
      <td className="py-3 px-4 text-xs text-gray-500 font-mono">{refNo}</td>
      <td className="py-3 px-4">
        <span className="inline-block px-2 py-0.5 text-[10px] font-medium rounded-full bg-indigo-50 text-indigo-700">
          Deposit
        </span>
      </td>
      <td className="py-3 px-4 text-xs text-gray-500 max-w-[200px] truncate">{description}</td>
      <td className="py-3 px-4 text-xs text-gray-500">—</td>
      <td className="py-3 px-4 text-xs font-semibold text-indigo-600 text-right">{formatCurrency(amount)}</td>
    </tr>
  );
}

// ─── Headers by type ────────────────────────────────────────

function getHeaders(type: string): string[] {
  if (type === "other-expenses") return ["Date", "Transaction ID", "Category", "Description", "Mode", "Amount"];
  if (type === "appointments") return ["Date", "Appointment ID", "Status", "Time Slot", "Mode", "Amount"];
  if (type === "deposit-logs") return ["Date", "Reference", "Type", "Description", "Mode", "Amount"];
  return ["Date", "Order No.", "Category", "Description", "Mode", "Amount"];
}

// ─── Main component ─────────────────────────────────────────

export default function TransactionTable({
  type,
  data,
  pagination,
  loading,
  currentPage,
  onPageChange,
  onRowClick,
}: TransactionTableProps) {
  const headers = getHeaders(type);
  const totalPages = pagination?.totalPages || 1;
  const totalItems = pagination?.totalItemsCount ?? pagination?.totalItems ?? 0;

  if (loading) {
    return (
      <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
        <div className="p-4">
          {Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className="flex gap-4 py-3 animate-pulse">
              {Array.from({ length: 6 }).map((_, j) => (
                <div key={j} className="h-4 bg-gray-100 rounded flex-1" />
              ))}
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="bg-gray-50/80">
              {headers.map((h) => (
                <th key={h} className="py-3 px-4 text-left text-[10px] font-semibold text-gray-500 uppercase tracking-wider">
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.length === 0 ? (
              <tr>
                <td colSpan={headers.length} className="py-12 text-center text-sm text-gray-400">
                  No records found for this period
                </td>
              </tr>
            ) : (
              data.map((item) => {
                if (type === "other-expenses") return renderExpenseRow(item as ExpenseItem, onRowClick);
                if (type === "appointments") return renderAppointmentRow(item as AppointmentPaymentItem, onRowClick);
                if (type === "deposit-logs") return renderDepositRow(item as BankDepositItem, onRowClick);
                return renderOrderRow(item as PaymentOrderItem, onRowClick);
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 0 && (
        <div className="flex items-center justify-between px-4 py-3 border-t border-gray-100">
          <p className="text-xs text-gray-500">
            Showing {data.length} of {totalItems} results
          </p>
          <div className="flex items-center gap-1">
            <button
              disabled={currentPage <= 1}
              onClick={() => onPageChange(currentPage - 1)}
              className="p-1.5 rounded-lg hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
            >
              <ChevronLeft className="w-4 h-4 text-gray-600" />
            </button>
            {Array.from({ length: Math.min(totalPages, 5) }).map((_, i) => {
              let pageNum: number;
              if (totalPages <= 5) {
                pageNum = i + 1;
              } else if (currentPage <= 3) {
                pageNum = i + 1;
              } else if (currentPage >= totalPages - 2) {
                pageNum = totalPages - 4 + i;
              } else {
                pageNum = currentPage - 2 + i;
              }
              return (
                <button
                  key={pageNum}
                  onClick={() => onPageChange(pageNum)}
                  className={`w-8 h-8 text-xs font-medium rounded-lg transition-colors
                    ${pageNum === currentPage
                      ? "bg-secondary text-white"
                      : "text-gray-600 hover:bg-gray-100"
                    }`}
                >
                  {pageNum}
                </button>
              );
            })}
            <button
              disabled={currentPage >= totalPages}
              onClick={() => onPageChange(currentPage + 1)}
              className="p-1.5 rounded-lg hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
            >
              <ChevronRight className="w-4 h-4 text-gray-600" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
